<script>
  import Note from "./Note.svelte";
  import Search from "./Search.svelte";
  import SearchHistory from "./SearchHistory.svelte";
  import { db } from "./firebase";
  import {
    query,
    where,
    orderBy,
    collection,
    addDoc,
    Firestore,
    getDocs,
    doc,
    deleteDoc,
    setDoc,
  } from "firebase/firestore";
  import { collectionData } from "rxfire/firestore";
  import { startWith } from "rxjs/operators";
  import { tap } from "rxjs/operators";
  import { onMount } from "svelte";

  // User ID passed from parent
  export let uid;

  let notes = [];
  let history = [];
  let tags = [];

  const fetchNotes = async () => {
    const notesCol = query(
      collection(db, "notes"),
      where("uid", "==", uid),
      orderBy("time", "desc")
    );
    const notesSnapshot = await getDocs(notesCol);
    const noteList = notesSnapshot.docs.map((doc) => {
      return {
        id: doc.id,
        visible: true,
        ...doc.data(),
      };
    });
    console.log(noteList);
    notes = noteList;

    // Collect all tags for autocomplete
    const tagsList = notes.reduce((acc, note) => {
      return [...acc, ...note.tags];
    }, []);
    tags = [...new Set(tagsList)];
  };

  async function addNote() {
    const blankNote = {
      uid,
      body: "",
      tags: [],
      files: [],
      time: Date.now(),
      collapsed: false,
    };
    const docRef = await addDoc(collection(db, "notes"), blankNote);
    const id = docRef.id;
    notes = [{ id, visible: true, ...blankNote }, ...notes];
  }

  const saveTimeoutDelay = 5000;
  let saving = false;
  let savingTimeout = {};

  function updateBody(event) {
    const { id, newBody } = event.detail;

    // Save note if it hasn't been changed for more than 5 seconds
    if (savingTimeout.id) {
      clearTimeout(savingTimeout.id);
    }

    savingTimeout.id = setTimeout(() => {
      const noteRef = doc(db, "notes", id);
      setDoc(noteRef, { body: newBody }, { merge: true });

      // Show saving indicator for a second
      saving = true;
      setTimeout(() => {
        saving = false;
      }, 500);
    }, saveTimeoutDelay);
  }

  async function removeNote(event) {
    const { id } = event.detail;
    if (
      confirm(
        "Are you sure you want to delete this note? This action cannot be undone."
      )
    ) {
      notes = notes.filter((note) => note.id !== id);
      await deleteDoc(doc(db, "notes", id));
    }
  }

  function collapseNote(event) {
    const { id, collapsed } = event.detail;
    const noteRef = doc(db, "notes", id);
    setDoc(noteRef, { collapsed: collapsed }, { merge: true });
  }

  function setTags(event) {
    const { id, tags } = event.detail;

    const noteRef = doc(db, "notes", id);
    setDoc(noteRef, { tags: tags }, { merge: true });
  }

  function setFiles(event) {
    const { id, files } = event.detail;

    const noteRef = doc(db, "notes", id);
    setDoc(noteRef, { files: files }, { merge: true });
  }

  let showSearch = false;
  function toggleSearchPopup() {
    showSearch = !showSearch;
    if (showSearch) {
      document.body.classList.add("noscroll");
    } else {
      document.body.classList.remove("noscroll");
    }
  }

  function handleShortcuts(event) {
    if (event.key == "s" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      toggleSearchPopup();
    }
    if (event.key == "e" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      addNote();
    }
  }

  //
  // Search handlers
  //
  function filterTag(event) {
    const { saveQuery, tag, index } = event.detail;

    notes = notes.map((note) => ({
      ...note,
      visible: note.tags.indexOf(tag) !== -1,
    }));

    if (saveQuery) {
      saveSearchQuery("tag", tag, null);
    } else if (index >= 0) {
      selectSearchQuery(index);
    }
  }

  function filterNote(event) {
    const { saveQuery, id, searchTerm, index } = event.detail;

    notes = notes.map((note) => ({ ...note, visible: note.id === id }));

    if (saveQuery) {
      saveSearchQuery("single", id, searchTerm);
    } else if (index >= 0) {
      selectSearchQuery(index);
    }
  }

  function filterNotes(event) {
    const { saveQuery, noteIds, searchTerm, index } = event.detail;

    notes = notes.map((note) => ({
      ...note,
      visible: noteIds.indexOf(note.id) !== -1,
    }));

    if (saveQuery) {
      saveSearchQuery("all", noteIds, searchTerm);
    } else if (index >= 0) {
      selectSearchQuery(index);
    }
  }

  function deleteSearch(event) {
    const { index } = event.detail;
    history = history.filter((_, i) => i !== index);
    localStorage.setItem(
      "searchhistory",
      JSON.stringify(history)
    );
  }

  function selectSearchQuery(index) {
    // Unhighlight all search queries and select the one at index
    if (history[index].active) {
      // actually, this is already active, reset the search
      notes = notes.map((note) => ({ ...note, visible: true }));
      history[index].active = false;
      return;
    }

    history = history.map((item) => ({ ...item, active: false }));
    history[index].active = true;
  }

  function saveSearchQuery(type, value, term) {
    // Unset active on all items
    history = history.map((item) => ({ ...item, active: false }));

    // Save to localstorage with new item
    localStorage.setItem(
      "searchhistory",
      JSON.stringify([{ type, value, term }, ...history])
    );

    // Add new item to history and set to active
    history = [
      {
        type,
        value,
        term,
        active: true,
      },
      ...history,
    ];
  }

  onMount(() => {
    fetchNotes();
    history = JSON.parse(localStorage.getItem("searchhistory")) || [];
  });
</script>

<svelte:window on:keydown={handleShortcuts} />

<section>
  <div class="button-bar">
    <button on:click={addNote}
      >Add Note<span class="shortcut">&nbsp;/&nbsp;⌘E</span></button
    >
    <button on:click={toggleSearchPopup}
      >Search<span class="shortcut">&nbsp;/&nbsp;⌘S</span></button
    >
  </div>

  {#if showSearch}
    <Search
      {notes}
      on:close={toggleSearchPopup}
      on:filterTag={filterTag}
      on:filterNote={filterNote}
      on:filterNotes={filterNotes}
    />
  {/if}

  <div class="save-indicator">
    {#if saving}💾{/if}
  </div>

  <SearchHistory
    {history}
    on:deleteSearch={deleteSearch}
    on:filterTag={filterTag}
    on:filterNote={filterNote}
    on:filterNotes={filterNotes}
  />

  {#each notes as note (note.id)}
    {#if note.visible}
      <Note
        {...note}
        allTags={tags}
        on:remove={removeNote}
        on:collapse={collapseNote}
        on:updateBody={updateBody}
        on:setTags={setTags}
        on:setFiles={setFiles}
      />
    {/if}
  {/each}
</section>

<style>
  :global(.noscroll) {
    overflow: hidden;
  }

  .save-indicator {
    position: fixed;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 100;
    font-size: 1.5rem;
  }
  button {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    font-weight: 600;
    border: 1px solid #ccc;
    background-color: transparent;
  }

  button .shortcut {
    color: #bbb;
    display: none;
  }
  @media (hover: hover) and (pointer: fine) {
    button .shortcut {
      display: inline;
    }
  }

  .button-bar {
    position: absolute;
    top: 20px;
    right: 1rem;
  }
</style>
